import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  INITIAL_FORM_STATE,
  FORM_STEPS,
  STUDY_LEVELS,
  PUBLICATION_GOALS,
  RESEARCH_FIELDS,
  RESEARCH_SUPPORT_BASE,
} from '../../constants/researchSupport';
import { sanitizeEmail, sanitizeFormObject, sanitizePhone, sanitizeText, countWords } from '../../utils/inputSanitizer';
import {
  loadDraft,
  saveDraft,
  submitResearchApplication,
} from '../../services/researchSubmissionService';
import { uploadResearchFile, validateResearchFile } from '../../utils/researchSupportUpload';
import LoadingIndicator from '../common/LoadingIndicator';

const inputClass =
  'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof';
const labelClass = 'block text-sm font-medium text-gray-700';

function ProgressBar({ currentStep }) {
  return (
    <ol className="flex flex-wrap gap-2 mb-8" aria-label="Application progress">
      {FORM_STEPS.map((step, index) => (
        <li
          key={step.id}
          className={`flex-1 min-w-[120px] text-center py-2 px-3 rounded-md text-xs sm:text-sm font-medium ${
            index === currentStep
              ? 'bg-iof text-white'
              : index < currentStep
              ? 'bg-iof/20 text-iof-dark'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          <span className="hidden sm:inline">{index + 1}. </span>
          {step.title}
        </li>
      ))}
    </ol>
  );
}

function ApplicationPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const autosaveTimer = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', {
        state: {
          from: location.pathname,
          message: 'Please sign in to submit a research application.',
        },
      });
      return;
    }

    (async () => {
      try {
        const draft = await loadDraft(user.uid);
        setForm((prev) => ({
          ...prev,
          ...draft,
          files: { ...INITIAL_FORM_STATE.files, ...(draft.files || {}) },
        }));
        setCurrentStep(draft.currentStep || 0);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingDraft(false);
      }
    })();
  }, [user, authLoading, navigate, location.pathname]);

  const persistDraft = useCallback(
    async (nextForm, step) => {
      if (!user) return;
      try {
        await saveDraft(user.uid, nextForm, step);
        setDraftSavedAt(new Date());
      } catch (e) {
        console.error('Draft save failed', e);
        if (e.message?.includes('Too many draft saves')) {
          setErrors((prev) => ({ ...prev, draft: e.message }));
        }
      }
    },
    [user]
  );

  useEffect(() => {
    if (!user || loadingDraft) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      persistDraft(form, currentStep);
    }, 1500);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [form, currentStep, user, loadingDraft, persistDraft]);

  const updateField = (name, value) => {
    const sanitizedValue =
      typeof value === 'string'
        ? name === 'email'
          ? sanitizeEmail(value)
          : name === 'phone'
          ? sanitizePhone(value)
          : sanitizeText(value)
        : value;

    setForm((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateStep = (step) => {
    const next = {};
    if (step === 0) {
      if (!form.fullName.trim()) next.fullName = 'Required';
      if (!form.email.trim()) next.email = 'Required';
      if (!form.phone.trim()) next.phone = 'Required';
      if (!form.institution.trim()) next.institution = 'Required';
      if (!form.department.trim()) next.department = 'Required';
      if (!form.levelOfStudy) next.levelOfStudy = 'Required';
      if (!form.country.trim()) next.country = 'Required';
      if (!form.graduationYear) next.graduationYear = 'Required';
    }
    if (step === 1) {
      if (!form.researchTitle.trim()) next.researchTitle = 'Required';
      if (!form.researchField) next.researchField = 'Required';
      if (!form.abstract.trim() || form.abstract.trim().length < 100) {
        next.abstract = 'Abstract must be at least 100 characters';
      }
      if (!form.keywords.trim()) next.keywords = 'Required';
      if (!form.publicationGoal) next.publicationGoal = 'Required';
    }
    if (step === 2) {
      if (!form.files?.thesisPdf?.url) next.thesisPdf = 'Thesis PDF is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFile = async (e, fileKey) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const validation = validateResearchFile(file, fileKey);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, [fileKey]: validation.error }));
      return;
    }

    setUploading((prev) => ({ ...prev, [fileKey]: true }));
    try {
      const uploaded = await uploadResearchFile(file, user.uid, fileKey);
      setForm((prev) => ({
        ...prev,
        files: { ...prev.files, [fileKey]: uploaded },
      }));
      setErrors((prev) => ({ ...prev, [fileKey]: '' }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fileKey]: err.message }));
    } finally {
      setUploading((prev) => ({ ...prev, [fileKey]: false }));
    }
  };

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, FORM_STEPS.length - 1));
  };

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    console.log('ApplicationPortal handleSubmit start', {
      honeypot,
      isSubmitting,
      currentStep,
      formState: {
        fullName: form.fullName,
        email: form.email,
        thesisPdf: form.files?.thesisPdf?.url,
      },
    });
    if (honeypot) {
      console.warn('Honeypot triggered, aborting submission');
      return;
    }
    if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
      console.warn('Submission validation failed', { errors });
      setErrors((prev) => ({
        ...prev,
        submit: 'Please complete all required fields before submitting.',
      }));
      return;
    }
    if (isSubmitting) {
      console.warn('Submission already in progress');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Submitting research application', {
        userId: user.uid,
        userEmail: user.email,
      });
      const result = await submitResearchApplication({
        userId: user.uid,
        userEmail: user.email,
        form: {
          ...form,
          ...sanitizeFormObject(form),
        },
      });
      console.log('Submission result', result);
      navigate(`${RESEARCH_SUPPORT_BASE}/success`, {
        state: { submissionId: result.id, researchTitle: result.researchTitle },
      });
    } catch (err) {
      console.error('Submission failed', err);
      setErrors((prev) => ({ ...prev, submit: err.message || 'Submission failed' }));
    } finally {
      setIsSubmitting(false);
      console.log('ApplicationPortal handleSubmit complete');
    }
  };

  if (authLoading || loadingDraft) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <ProgressBar currentStep={currentStep} />
      {draftSavedAt && (
        <p className="text-xs text-gray-500 mb-4" aria-live="polite">
          Draft saved at {draftSavedAt.toLocaleTimeString()}
        </p>
      )}
      {errors.draft && (
        <p className="text-xs text-amber-700 mb-4" role="alert">
          {errors.draft}
        </p>
      )}

      {/* Honeypot spam field */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(sanitizeText(e.target.value))}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {currentStep === 0 && (
        <section className="grid sm:grid-cols-2 gap-4">
          {[
            ['fullName', 'Full name', 'text'],
            ['email', 'Email', 'email'],
            ['phone', 'Phone number', 'tel'],
            ['institution', 'Institution', 'text'],
            ['department', 'Department', 'text'],
            ['country', 'Country', 'text'],
            ['graduationYear', 'Graduation year', 'number'],
          ].map(([name, label, type]) => (
            <div key={name} className={name === 'fullName' ? 'sm:col-span-2' : ''}>
              <label htmlFor={name} className={labelClass}>
                {label}
              </label>
              <input
                id={name}
                type={type}
                inputMode={name === 'phone' ? 'tel' : undefined}
                pattern={name === 'phone' ? '[0-9+\-\s()]+' : undefined}
                maxLength={name === 'phone' ? 20 : undefined}
                value={form[name]}
                onChange={(e) => updateField(name, e.target.value)}
                className={inputClass}
              />
              {errors[name] && <p className="text-sm text-red-600 mt-1">{errors[name]}</p>}
            </div>
          ))}
          <div className="sm:col-span-2">
            <label htmlFor="levelOfStudy" className={labelClass}>
              Level of study
            </label>
            <select
              id="levelOfStudy"
              value={form.levelOfStudy}
              onChange={(e) => updateField('levelOfStudy', e.target.value)}
              className={inputClass}
            >
              <option value="">Select level</option>
              {STUDY_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            {errors.levelOfStudy && (
              <p className="text-sm text-red-600 mt-1">{errors.levelOfStudy}</p>
            )}
          </div>
        </section>
      )}

      {currentStep === 1 && (
        <section className="space-y-4">
          <div>
            <label htmlFor="researchTitle" className={labelClass}>
              Research title
            </label>
            <input
              id="researchTitle"
              value={form.researchTitle}
              onChange={(e) => updateField('researchTitle', e.target.value)}
              className={inputClass}
            />
            {errors.researchTitle && (
              <p className="text-sm text-red-600 mt-1">{errors.researchTitle}</p>
            )}
          </div>
          <div>
            <label htmlFor="researchField" className={labelClass}>
              Research field
            </label>
            <select
              id="researchField"
              value={form.researchField}
              onChange={(e) => updateField('researchField', e.target.value)}
              className={inputClass}
            >
              <option value="">Select field</option>
              {RESEARCH_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {errors.researchField && (
              <p className="text-sm text-red-600 mt-1">{errors.researchField}</p>
            )}
          </div>
          <div>
            <label htmlFor="abstract" className={labelClass}>
              Abstract / summary
            </label>
            <textarea
              id="abstract"
              rows={6}
              value={form.abstract}
              onChange={(e) => updateField('abstract', e.target.value)}
              className={inputClass}
            />
            <p className="text-sm text-gray-500 mt-1">
              {countWords(form.abstract)} words. Recommended 150–400 words.
            </p>
            {errors.abstract && <p className="text-sm text-red-600 mt-1">{errors.abstract}</p>}
          </div>
          <div>
            <label htmlFor="keywords" className={labelClass}>
              Keywords (comma-separated)
            </label>
            <input
              id="keywords"
              value={form.keywords}
              onChange={(e) => updateField('keywords', e.target.value)}
              className={inputClass}
            />
            {errors.keywords && <p className="text-sm text-red-600 mt-1">{errors.keywords}</p>}
          </div>
          <div>
            <label htmlFor="publicationGoal" className={labelClass}>
              Publication goal
            </label>
            <select
              id="publicationGoal"
              value={form.publicationGoal}
              onChange={(e) => updateField('publicationGoal', e.target.value)}
              className={inputClass}
            >
              <option value="">Select goal</option>
              {PUBLICATION_GOALS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            {errors.publicationGoal && (
              <p className="text-sm text-red-600 mt-1">{errors.publicationGoal}</p>
            )}
          </div>
          <div>
            <label htmlFor="linkedIn" className={labelClass}>
              LinkedIn / profile (optional)
            </label>
            <input
              id="linkedIn"
              type="url"
              value={form.linkedIn}
              onChange={(e) => updateField('linkedIn', e.target.value)}
              className={inputClass}
              placeholder="https://linkedin.com/in/..."
            />
          </div>
        </section>
      )}

      {currentStep === 2 && (
        <section className="space-y-6">
          {[
            { key: 'thesisPdf', label: 'Thesis / project PDF (required)', required: true },
            { key: 'supportingDocs', label: 'Supporting documents (optional)', required: false },
            { key: 'dataset', label: 'Optional dataset', required: false },
          ].map(({ key, label, required }) => (
            <div key={key} className="border border-gray-200 rounded-lg p-4">
              <label className={labelClass}>{label}</label>
              <input
                type="file"
                accept={key === 'thesisPdf' ? '.pdf' : undefined}
                onChange={(e) => handleFile(e, key)}
                className="mt-2 block w-full text-sm"
                disabled={uploading[key]}
              />
              {uploading[key] && <p className="text-sm text-gray-500 mt-1">Uploading…</p>}
              {form.files?.[key]?.url && (
                <p className="text-sm text-green-700 mt-1">
                  ✓ {form.files[key].originalName || 'Uploaded'}
                </p>
              )}
              {errors[key] && <p className="text-sm text-red-600 mt-1">{errors[key]}</p>}
              {!required && (
                <p className="text-xs text-gray-500 mt-1">PDF, DOCX, or images accepted</p>
              )}
            </div>
          ))}
        </section>
      )}

      {currentStep === 3 && (
        <section className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-3 text-sm">
          <h2 className="font-semibold text-gray-900">Review your application</h2>
          <p>
            <strong>Title:</strong> {form.researchTitle}
          </p>
          <p>
            <strong>Field:</strong> {form.researchField}
          </p>
          <p>
            <strong>Institution:</strong> {form.institution}, {form.country}
          </p>
          <p>
            <strong>Publication goal:</strong> {form.publicationGoal}
          </p>
          <p className="text-gray-600">
            By submitting, you confirm this is your original work and the information provided is
            accurate.
          </p>
        </section>
      )}

      {errors.submit && (
        <p className="text-red-600 text-sm mt-4" role="alert">
          {errors.submit}
        </p>
      )}

      <div className="flex flex-wrap gap-3 mt-8">
        {currentStep > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
        )}
        {currentStep < FORM_STEPS.length - 1 ? (
          <button
            type="button"
            onClick={goNext}
            className="px-6 py-2 bg-iof text-white rounded-md hover:bg-iof-dark"
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-8 py-2 bg-iof text-white rounded-md hover:bg-iof-dark disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting…' : 'Submit application'}
          </button>
        )}
        <Link
          to={`${RESEARCH_SUPPORT_BASE}/guidelines`}
          className="px-4 py-2 text-iof text-sm hover:underline self-center"
        >
          View guidelines
        </Link>
      </div>
    </div>
  );
}

export default ApplicationPortal;
